import { useContext, useState } from 'react';
import { HubDetailsContext } from '../../context/hub-details';
import { Box, Container, Divider, Grid, Stack, Toolbar, Typography } from '@mui/material';
import { AnalyticsDashboardTile } from './analytics-dashboard-tile';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import moment from 'moment/moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

export const AnalyticsPage = () => {

  const hubContext = useContext(HubDetailsContext);

  const [popularityDate, setPopularityDate] = useState(moment().subtract(1, 'month').endOf('month'));
  const [availabilityDate, setAvailabilityDate] = useState(moment().subtract(1, 'month').endOf('month'));
  const [pricingDate, setPricingDate] = useState(moment().subtract(1, 'month').endOf('month'));

  return (
    <>
      <Toolbar />
      <Box
        sx={{
          height: '45vh',
          mt: 2,
          backgroundImage: `url(${hubContext.hubDetails.hub_styles?.banner_image_url || '/hub_banner_1.jpg'})`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={2} style={{ height: '45vh', alignItems: 'center' }}>
            <Grid item xs={12} md={7} lg={7}>
              <Typography
                variant="h3"
                component="h1"
                sx={{ mb: 2, textAlign: 'left', fontWeight: 600, color: "common.white" }}
              >
                BNG Data Studio
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 2, textAlign: 'left', color: "common.white" }}
              >
                Use the page below to view BNG unit analytics.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Container maxWidth="lg" sx={{ pt: 8, pb: 8 }}>

        <Stack direction={'row'} justifyContent={'space-between'} sx={{ borderRadius: '10px', padding: 2 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2 }}
          >
            Popularity breakdown
          </Typography>

          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={'en-GB'}>
            <DatePicker label={'Select a date'} views={['year', 'month']}
              minDate={moment('2024-02-01')}
              maxDate={moment().subtract(1, 'month').endOf('month')}
              value={popularityDate}
              onChange={(newValue) => setPopularityDate(moment(newValue))} />
          </LocalizationProvider>
        </Stack>

        <Stack direction={'row'} justifyContent={'space-between'}>
          <AnalyticsDashboardTile
            title={'Most Popular Regions'}
            groupBy={'region'}
            isSearchAnalytics={true}
            customValueString={'Total'}
            searchMonth={(popularityDate.month() + 1).toString()}
            searchYear={popularityDate.year()}
            valueFormatStringValue={'%'}
            allowDownload={true}
          ></AnalyticsDashboardTile>

          <AnalyticsDashboardTile
            title={'Most Popular Unit Type\'s'}
            groupBy={'unit_type'}
            isSearchAnalytics={true}
            customValueString={'Total'}
            searchMonth={(popularityDate.month() + 1).toString()}
            searchYear={popularityDate.year()}
            valueFormatStringValue={'%'}
            useDonutChart={true}
            allowDownload={true}
          ></AnalyticsDashboardTile>
        </Stack>

        <AnalyticsDashboardTile
          title={'Most popular Local Planning Authorities (Top 10)'}
          groupBy={'lpa'}
          isSearchAnalytics={true}
          customValueString={'Total'}
          searchMonth={(popularityDate.month() + 1).toString()}
          searchYear={popularityDate.year()}
          valueFormatStringValue={'%'}
          allowDownload={true}
        ></AnalyticsDashboardTile>

        <Divider sx={{ mt: 4, mb: 4 }}></Divider>

        <Stack direction={'row'} justifyContent={'space-between'} sx={{ borderRadius: '10px', padding: 2 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2 }}
          >
            Availability breakdown
          </Typography>

          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={'en-GB'}>
            <DatePicker label={'Select a date'} views={['year', 'month']}
              minDate={moment('2025-01-01')}
              maxDate={moment().subtract(1, 'month').endOf('month')}
              value={availabilityDate}
              onChange={(newValue) => setAvailabilityDate(moment(newValue))} />

          </LocalizationProvider>
        </Stack>

        <Stack direction={'row'} justifyContent={'space-between'}>
          <AnalyticsDashboardTile
            title={'Unit availability by Region'}
            groupBy={'region'}
            customValueString={'Total'}
            searchMonth={(availabilityDate.month() + 1).toString()}
            searchYear={availabilityDate.year()}
            valueFormatStringValue={'%'}
            available={true}
            allowDownload={true}
          ></AnalyticsDashboardTile>

          <AnalyticsDashboardTile
            title={'Unit availability by Unit Type'}
            groupBy={'unit_type'}
            customValueString={'Total'}
            available={true}
            searchMonth={(availabilityDate.month() + 1).toString()}
            searchYear={availabilityDate.year()}
            valueFormatStringValue={'%'}
            useDonutChart={true}
            allowDownload={true}
          ></AnalyticsDashboardTile>
        </Stack>

        {/* <AnalyticsDashboardTile
          title={'Unit availability by Local Planning Authority'}
          groupBy={'lpa'}
          customValueString={'Total'}
          available={true}
          valueFormatStringValue={'%'}
        ></AnalyticsDashboardTile> */}

        <Divider sx={{ mt: 4, mb: 4 }}></Divider>

        <Stack direction={'row'} justifyContent={'space-between'} sx={{ borderRadius: '10px', padding: 2 }}>
          <Typography
            variant="h6"
            sx={{ mb: 2 }}
          >
            Average pricing information
          </Typography>

          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={'en-GB'}>
            <DatePicker label={'Select a date'} views={['year', 'month']}
              minDate={moment('2025-01-01')}
              maxDate={moment().subtract(1, 'month').endOf('month')}
              value={pricingDate}
              onChange={(newValue) => setPricingDate(moment(newValue))} />
          </LocalizationProvider>
        </Stack>

        <Stack direction={'row'} justifyContent={'space-between'}>
          <AnalyticsDashboardTile
            title={'Average Unit Price by Region'}
            groupBy={'region'}
            isSearchAnalytics={false}
            searchMonth={(pricingDate.month() + 1).toString()}
            searchYear={pricingDate.year()}
            customValueString={'Average unit price (£)'}
            allowDownload={true}
          ></AnalyticsDashboardTile>

          <AnalyticsDashboardTile
            title={'Average Unit Price by Unit Type'}
            groupBy={'unit_type'}
            isSearchAnalytics={false}
            searchMonth={(pricingDate.month() + 1).toString()}
            searchYear={pricingDate.year()}
            customValueString={'Average unit price (£)'}
            allowDownload={true}
          ></AnalyticsDashboardTile>
        </Stack>

        <AnalyticsDashboardTile
          title={'Average Unit Price by Local Planning Authority'}
          groupBy={'lpa'}
          searchMonth={(pricingDate.month() + 1).toString()}
          searchYear={pricingDate.year()}
          isSearchAnalytics={false}
          customValueString={'Average unit price (£)'}
          allowDownload={true}
        ></AnalyticsDashboardTile>
      </Container>
    </>
  );
};